import type { SegmentUrlResolver } from '@mimir/client-common/util/segmentUrlCache';
import type { StreamRoleId } from '@mimir/common/dto/mediaAvailability';
import { invariant } from '@mimir/common/util/invariant';
import { getSegmentIndexFromSegmentTime, initializationSegmentIndex } from '@mimir/common/util/segmentIndexing';
import type { BufferFetching, FragmentedMp4Provider } from './fetching';
import type { FragmentedMp4Buffers } from '@mimir/common/mp4/fragmentedMp4Types';

/*
  Kelda live ingest produces CMAF segments which are Fragmented MP4 files which
  can be fed directly to the MSE media source buffer.
*/
export class CmafSegmentFetcher implements FragmentedMp4Provider {
  constructor(private urlResolver: SegmentUrlResolver, private fetching: BufferFetching, readonly itemId: string) {
  }

  async getMediaSegmentBuffers(needInitialization: boolean, streamId: StreamRoleId, segmentTime: number): Promise<FragmentedMp4Buffers> {
    const getUrls: Promise<unknown>[] = [];
    let initUrl: string | null = null;
    let indexedUrl: string | null = null;

    // Only fetch initialization URL if we don't have a buffer already.
    if (needInitialization) {
      getUrls.push(
        this.urlResolver.getMediaSegmentUrl(
          this.itemId,
          streamId,
          initializationSegmentIndex
        ).then(url => initUrl = url)
      );
    }

    getUrls.push(
      this.urlResolver.getMediaSegmentUrl(
        this.itemId,
        streamId,
        getSegmentIndexFromSegmentTime(segmentTime, true),
      ).then(url => indexedUrl = url)
    );

    await Promise.all(getUrls);

    invariant(indexedUrl != null, 'Expected indexedUrl');

    const getBuffers: Promise<unknown>[] = [];

    let initialization: BufferSource | null = null;
    let mediaSegment: BufferSource | null = null;

    // Only fetch the initialization buffer if we don't have it already.
    if (needInitialization) {
      invariant(initUrl, 'Expected initUrl when no initBuf yet');
      getBuffers.push(
        this.fetching.getBuffer(initUrl).then(buf => initialization = buf)
      );
    }

    getBuffers.push(
      this.fetching.getBuffer(indexedUrl).then(buf => mediaSegment = buf)
    );
    await Promise.all(getBuffers);

    invariant(initialization || !needInitialization, 'Expected initBuf when needed');
    invariant(mediaSegment, 'Expected indexedBuf');
    return { initialization, mediaSegment, info: { initUrl, indexedUrl } };
  }

  cleanup() {
  }
}
